import React from 'react'
import styled from 'styled-components'
import { MapControl } from '../../../atoms/MapControl'
import { Icon } from '../../../atoms/Icon'

const MapControls = styled.div<{ $position: string }>`
  position: absolute;
  ${({ $position }) => {
    switch ($position) {
      case 'top-left':
        return 'top: 16px; left: 16px;'
      case 'top-right':
        return 'top: 16px; right: 16px;'
      case 'bottom-left':
        return 'bottom: 16px; left: 16px;'
      case 'bottom-right':
        return 'bottom: 16px; right: 16px;'
      default:
        return 'top: 16px; right: 16px;'
    }
  }}
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 6px;
  }
`

const ControlGroup = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const StyledMapControl = styled(MapControl)<{ $active?: boolean }>`
  border-radius: 0;
  box-shadow: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: ${({ $active }) => ($active ? 'var(--primary, #0294df)' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--text-primary, #1a1a1a)')};

  &:last-child {
    border-bottom: none;
  }

  &:first-child {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  &:last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  &:hover {
    background: ${({ $active }) =>
      $active ? 'var(--primary-hover, #0288cd)' : '0 2px 8px rgba(0,0,0,0.2)'};
    transform: ${({ $active }) => ($active ? 'none' : 'translateY(-1px)')};
  }
`

interface MapControlsGroupProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  showLayersButton?: boolean
  showLegendButton?: boolean
  show3DButton?: boolean
  showPrintButton?: boolean
  showSearchButton?: boolean
  showFullScreenButton?: boolean
  controlGroups?: {
    main?: Array<'layers' | 'legend' | '3d' | 'print' | 'fullscreen' | 'search'>
    secondary?: Array<string>
  }
  layersPanelOpen?: boolean
  onLayersClick?: () => void
  onLegendClick?: () => void
  on3DClick?: () => void
  onPrintClick?: () => void
  onSearchClick?: () => void
  onFullScreenClick?: () => void
}

const controlConfigs = {
  layers: {
    icon: <Icon name="layers" size="medium" />,
    title: 'Layers',
    key: 'layers' as const,
  },
  legend: {
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16">
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="2"
          ry="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <rect x="7" y="7" width="3" height="3" fill="currentColor" />
        <rect x="7" y="14" width="3" height="3" fill="currentColor" />
        <line x1="14" y1="8.5" x2="17" y2="8.5" stroke="currentColor" strokeWidth="2" />
        <line x1="14" y1="15.5" x2="17" y2="15.5" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    title: 'Legend',
    key: 'legend' as const,
  },
  '3d': {
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16">
        <path
          d="M12 2L2 9v11l10 7 10-7V9L12 2z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path d="M12 22V12" stroke="currentColor" strokeWidth="2" />
        <path d="M12 12L2 9" stroke="currentColor" strokeWidth="2" />
        <path d="M12 12l10-3" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    title: '3D View',
    key: '3d' as const,
  },
  print: {
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16">
        <path d="M6 9V2h12v7" stroke="currentColor" strokeWidth="2" fill="none" />
        <path
          d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
        <path d="M6 14h12v8H6z" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
    ),
    title: 'Print',
    key: 'print' as const,
  },
  fullscreen: {
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16">
        <path
          d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    ),
    title: 'Fullscreen',
    key: 'fullscreen' as const,
  },
  search: {
    icon: <Icon name="search" size="medium" />,
    title: 'Search',
    key: 'search' as const,
  },
}

export const MapControlsGroup: React.FC<MapControlsGroupProps> = ({
  position = 'top-right',
  showLayersButton = true,
  showLegendButton = true,
  show3DButton = true,
  showPrintButton = true,
  showSearchButton = false,
  showFullScreenButton = false,
  controlGroups,
  layersPanelOpen = false,
  onLayersClick,
  onLegendClick,
  on3DClick,
  onPrintClick,
  onSearchClick,
  onFullScreenClick,
}) => {
  const handleControlClick = (controlKey: string) => {
    switch (controlKey) {
      case 'layers':
        onLayersClick?.()
        break
      case 'legend':
        onLegendClick?.()
        break
      case '3d':
        on3DClick?.()
        break
      case 'print':
        onPrintClick?.()
        break
      case 'search':
        onSearchClick?.()
        break
      case 'fullscreen':
        onFullScreenClick?.()
        break
    }
  }

  const renderControl = (controlKey: string) => {
    const config = controlConfigs[controlKey as keyof typeof controlConfigs]
    if (!config) return null

    // Check if this control should show as active
    const isActive = controlKey === 'layers' && layersPanelOpen

    return (
      <StyledMapControl
        key={controlKey}
        icon={config.icon}
        title={config.title}
        onClick={() => handleControlClick(controlKey)}
        $active={isActive}
      />
    )
  }

  const renderDefaultControls = () => {
    const controls = []

    if (showLayersButton) controls.push('layers')
    if (showLegendButton) controls.push('legend')
    if (show3DButton) controls.push('3d')
    if (showSearchButton) controls.push('search')
    if (showPrintButton) controls.push('print')
    if (showFullScreenButton) controls.push('fullscreen')

    return controls.map(renderControl)
  }

  const renderCustomGroups = () => {
    if (!controlGroups) return renderDefaultControls()

    const groups = []

    // Main group
    if (controlGroups.main && controlGroups.main.length > 0) {
      groups.push(<ControlGroup key="main">{controlGroups.main.map(renderControl)}</ControlGroup>)
    }

    // Secondary group
    if (controlGroups.secondary && controlGroups.secondary.length > 0) {
      groups.push(
        <ControlGroup key="secondary">{controlGroups.secondary.map(renderControl)}</ControlGroup>
      )
    }

    return groups.length > 0 ? groups : renderDefaultControls()
  }

  return (
    <MapControls $position={position}>
      {controlGroups ? (
        renderCustomGroups()
      ) : (
        <ControlGroup>{renderDefaultControls()}</ControlGroup>
      )}
    </MapControls>
  )
}
